import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  TextField,
  Typography,
  DialogActions,
  Autocomplete,
} from '@mui/material'

import { HighlightOff as HighlightOffIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import moment from 'moment'
import { DROPDOWN_DEFAULT_VALUE } from '../utils/autoCompute'
import { queryService } from '../../../services/queryService'
import { activityTemplateService } from '../../../services/activityTemplateService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { formatUserDisplayName } from '../../../utility/userName'

const QueryDialog = ({
  processId,
  activityTemplateId,
  isQueryDialogOpen,
  setIsQueryDialogOpen,
}) => {
  const { user } = useAuthContext()
  const [newQueryQuestion, setNewQueryQuestion] = useState('')
  const [queries, setQueries] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [templateUsers, setTemplateUsers] = useState([])
  const [selectedQueryUser, setSelectedQueryUser] = useState(DROPDOWN_DEFAULT_VALUE)

  // If template has team-filtered users, use them. Otherwise fall back to all users.
  const dropdownUsers = templateUsers.length > 0 ? templateUsers : allUsers

  const fetchQueryData = async () => {
    const [queryData, templateData] = await Promise.all([
      queryService.getQuery(user, processId),
      activityTemplateService.getTemplate(user, activityTemplateId),
    ])

    const fetchedTemplateUsers = templateData?.data?.users || []

    setQueries(queryData.queries)
    setAllUsers(queryData.users)
    setTemplateUsers(fetchedTemplateUsers)
    setNewQueryQuestion('')
    setSelectedQueryUser(DROPDOWN_DEFAULT_VALUE)
  }

  useEffect(() => {
    ; (async () => {
      await fetchQueryData()
    })()
  }, [])

  const handleSaveQuery = async () => {
    if (selectedQueryUser == DROPDOWN_DEFAULT_VALUE) {
      return
    }
    await queryService.saveQuery(user, {
      process_id: processId,
      question_by: user.id,
      question: newQueryQuestion,
      answer_by: selectedQueryUser,
    })
    await fetchQueryData()
  }

  const deleteQuery = async (queryId) => {
    await queryService.deleteQuery(user, queryId)
    await fetchQueryData()
  }

  const handleSelectQueryUser = (_e, newValue) => {
    if (!newValue) {
      setSelectedQueryUser(DROPDOWN_DEFAULT_VALUE)
      return
    }
    const userId = newValue.user_id ?? newValue.id
    if (user?.id != userId) {
      setSelectedQueryUser(userId)
    } else {
      setSelectedQueryUser(DROPDOWN_DEFAULT_VALUE)
    }
  }

  const handleClose = () => {
    setIsQueryDialogOpen(false)
    setSelectedQueryUser(DROPDOWN_DEFAULT_VALUE)
    setNewQueryQuestion('')
  }

  return (
    <Dialog open={isQueryDialogOpen} fullWidth maxWidth="md">
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography className="textPrimary fontWeight700 fontsize20" variant="h5">
            Queries
          </Typography>
          <IconButton align="right" onClick={handleClose} size="small">
            <HighlightOffIcon />
          </IconButton>
        </Grid>
      </DialogTitle>
      <DialogContent>
        {queries.map((q) => {
          const u = allUsers.find((au) => au.id === q.answer_by)

          return (
            <div key={q.id}>
              <Typography variant="body1">
                <strong>Q{q.id}</strong>
                {`. ${q.question} `}
                {q.is_open && (
                  <Tooltip title="Delete/retract query?">
                    <IconButton size="small" onClick={() => deleteQuery(q.id)}>
                      <DeleteIcon size="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Typography>
              <Typography variant="body1">
                <strong>A</strong>
                {`. ${q.is_open ? 'Not answered yet.' : q.answer}`}
              </Typography>
              {u?.first_name && (
                <Typography sx={{ fontStyle: 'italic' }} variant="caption">
                  {formatUserDisplayName(u)}
                  {!q.is_open &&
                    ` on ${moment(new Date(q.question_at)).format('DD MMM YYYY, hh:mm A')}`}
                </Typography>
              )}
              <br />
              <br />
            </div>
          )
        })}
        <TextField
          value={newQueryQuestion}
          fullWidth
          multiline
          size="small"
          variant="outlined"
          color="primary"
          label={'Overall Comment'}
          style={{ marginTop: '.7rem' }}
          minRows={3}
          onChange={(e) => setNewQueryQuestion(e.target.value)}
        />

        <Autocomplete
          options={dropdownUsers}
          getOptionLabel={(option) =>
            option.full_name && option.email
              ? `${option.full_name} (${option.email})`
              : option.full_name || option.email || ''
          }
          value={
            dropdownUsers.find(
              (u) => u.user_id === selectedQueryUser || u.id === selectedQueryUser,
            ) || null
          }
          fullWidth
          style={{ marginTop: '.5rem' }}
          onChange={(e, newValue) => handleSelectQueryUser(e, newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              color="primary"
              size="small"
              label={'Select User to Answer Query'}
            />
          )}
        />
        <DialogActions>
          <Button autoFocus variant="outlined" color="primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button className="primary-btn" color="info" variant="outlined" onClick={handleSaveQuery}>
            Add New Query
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}

export default QueryDialog
